// US-090: row builder for products added to the Create-PO basket via the
// "Add Products" search dialog, rather than by ticking a row in the suggestion list.
//
// Shares the Phase-1 row shape (and cost ladder) with buildFastProductRow — the same
// GET /supplier-products/supplier/:id payload backs both — and differs only in how the
// quantity starts out, since a searched-added product is one the buyer asked for by
// name/SKU rather than one the forecast proposed:
//
//  - `quantity` is seeded from the supplier's minimum order quantity (falling back to 1),
//    matching what AddProductsDialog already does on the Edit-PO page.
//  - `userEdited` starts true, so a Phase 2 report merge can't silently replace that
//    deliberate quantity with a forecast suggestion.
//  - `suggestionState` starts 'unavailable': the variant generally isn't on the
//    suggestion list's current page, so Phase 2 would never reach it. If it does show up
//    on a later page, toFastRow() hands back this same row object and the normal merge
//    fills the report-only columns in.

import { buildFastProductRow, BuildFastProductRowOptions } from './buildFastProductRow';

/**
 * Build a Create-PO basket row from one supplier-product record chosen in the
 * Add Products dialog.
 */
/**
 * Build basket rows for a batch of products chosen in the Add Products dialog.
 *
 * No dedupe here: the dialog is bound with the PO's variant ids as `excludeVariantIds`,
 * so a product already on the PO can't be listed, let alone ticked, and Vuetify keys its
 * selection by variant_id so a within-batch duplicate can't occur either. The caller
 * merges through applyTableSelectionDiff, which keeps the stored row for a key it
 * already holds, so a duplicate that did somehow arrive still couldn't clobber edits.
 */
export function buildSearchAddedRows(
  products: any[],
  costOptsFor: (variantId: number) => BuildFastProductRowOptions,
): any[] {
  return products.map((p) => buildSearchAddedRow(p, costOptsFor(Number(p.variant_id))));
}

export function buildSearchAddedRow(p: any, opts: BuildFastProductRowOptions): any {
  const row = buildFastProductRow(p, opts);
  const quantity = Number(p['[min_order_quantity]']) || 1;
  return {
    ...row,
    quantity,
    total: quantity * (Number(row['[average_unit_cost]']) || 0),
    userEdited: true,
    suggestionState: 'unavailable',
  };
}
